import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Laugh, Crown, Vote, Check } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { audio } from "@/lib/audio";
import { Avatar, TimerRing, useCountdown } from "@/lib/gameUi";

export function MemeImage({ meme, className = "" }) {
  return (
    <div style={{ aspectRatio: `${meme.w} / ${meme.h}` }} className={`rounded-3xl overflow-hidden bg-black/60 border-4 border-white/90 shadow-[0_0_40px_rgba(255,42,133,0.35)] max-w-full ${className}`}>
      <img data-testid="meme-image" src={mediaUrl(meme.image)} alt={meme.name} className="w-full h-full object-contain" />
    </div>
  );
}

function useTicks(timeLeft) {
  const last = useRef(null);
  useEffect(() => {
    const s = Math.ceil(timeLeft);
    if (s !== last.current && s > 0 && s <= 10) { last.current = s; audio.tick(s <= 5); }
  }, [timeLeft]);
}

function MemeHeader({ state, label, timeLeft }) {
  const r = state.round;
  return (
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="glass rounded-full px-5 py-2 font-display font-bold text-lg border border-white/10 flex items-center gap-2"><Laugh className="w-5 h-5 text-[#FF2A85]" />R{r.index + 1}·Meme {r.q_index + 1}<span className="text-white/40">/{r.q_total}</span></span>
        <span data-testid="meme-phase-label" className="rounded-full px-5 py-2 font-head font-bold text-[#0D0714] bg-[#FFD700]">{label}</span>
        {state.meme?.rating === "adult" && <span className="rounded-full px-4 py-2 font-head font-bold text-white bg-rose-600">18+</span>}
      </div>
      {timeLeft != null && <TimerRing timeLeft={timeLeft} limit={state.current.time_limit} size={96} />}
    </div>
  );
}

export function MemePickTV({ state }) {
  const cur = state.current;
  const m = state.meme || {};
  const timeLeft = useCountdown(state.time_left, cur.time_limit);
  useTicks(timeLeft);
  useEffect(() => { audio.narrate("Caption this meme! Pick your funniest card."); return () => audio.stopNarration(); }, [cur.id]);
  const played = new Set(m.played_ids || []);
  return (
    <div data-testid="tv-meme-pick" className="h-full px-6 lg:px-16 py-10 flex flex-col">
      <MemeHeader state={state} label="Pick your funniest caption" timeLeft={timeLeft} />
      <motion.div key={cur.id} initial={{ scale: 0.92, opacity: 0, rotate: -1.5 }} animate={{ scale: 1, opacity: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 160, damping: 18 }} className="flex-1 min-h-0 flex items-center justify-center">
        <MemeImage meme={cur.meme} className="h-[60vh] mx-auto" />
      </motion.div>
      <div className="mt-5 flex items-center gap-4 flex-wrap">
        <span data-testid="tv-meme-played-count" className="glass rounded-full px-5 py-2 font-display font-bold text-lg border border-white/10">{m.played_count}/{m.total} played</span>
        <div className="flex gap-2 flex-wrap">{state.players.map((p) => (
          <div key={p.id} className={`transition-opacity duration-300 ${played.has(p.id) ? "opacity-100" : "opacity-30"}`}><Avatar player={p} size={40} /></div>
        ))}</div>
      </div>
    </div>
  );
}

export function MemeVoteTV({ state }) {
  const cur = state.current;
  const m = state.meme || {};
  const timeLeft = useCountdown(state.time_left, cur.time_limit);
  useTicks(timeLeft);
  useEffect(() => { audio.narrate("Time to vote! Which caption is the funniest?"); return () => audio.stopNarration(); }, [cur.id]);
  return (
    <div data-testid="tv-meme-vote" className="h-full px-6 lg:px-16 py-10 flex flex-col">
      <MemeHeader state={state} label="Vote on your phone" timeLeft={timeLeft} />
      <div className="grid lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] gap-8 flex-1 min-h-0">
        <MemeImage meme={cur.meme} className="h-[60vh] mx-auto self-center" />
        <div className="flex flex-col gap-3 min-h-0 overflow-hidden justify-center">
          {(m.entries || []).map((e, i) => (
            <motion.div key={e.id} data-testid={`tv-meme-entry-${i}`} initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.15 + i * 0.12 }}
              className="bg-white text-[#0D0714] rounded-2xl px-5 py-4 flex items-start gap-4 shadow-[6px_6px_0_#FF2A85]">
              <span className="font-display font-black text-2xl text-[#FF2A85] w-8 shrink-0">{i + 1}</span>
              <span className="font-head font-bold text-xl lg:text-2xl leading-snug">{e.text}</span>
            </motion.div>
          ))}
        </div>
      </div>
      <div className="mt-5 flex items-center gap-3">
        <Vote className="w-6 h-6 text-[#00F0FF]" />
        <span data-testid="tv-meme-voted-count" className="glass rounded-full px-5 py-2 font-display font-bold text-lg border border-white/10">{m.voted_count}/{m.voter_total} voted</span>
      </div>
    </div>
  );
}

export function MemeRevealTV({ state }) {
  const cur = state.current;
  const results = state.meme?.results || [];
  const winners = results.filter((r) => r.winner);
  useEffect(() => {
    audio.fanfare();
    if (winners.length) audio.narrate(`${winners.map((w) => w.owner_name).join(" and ")} wins with: ${winners[0].text}`);
    return () => audio.stopNarration();
  }, [cur.id]);
  return (
    <div data-testid="tv-meme-reveal" className="h-full px-6 lg:px-16 py-10 flex flex-col">
      <div className="grid lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] gap-8 flex-1 min-h-0">
        <div className="flex flex-col min-h-0 justify-center">
          <MemeImage meme={cur.meme} className="h-[48vh] mx-auto" />
          {winners[0] && <motion.p initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }} data-testid="tv-meme-winning-caption" className="mt-4 bg-white text-[#0D0714] rounded-2xl px-5 py-4 font-head font-black text-2xl lg:text-3xl text-center shadow-[6px_6px_0_#FFD700]">{winners[0].text}</motion.p>}
        </div>
        <div className="flex flex-col min-h-0 overflow-hidden">
          <h3 className="font-head uppercase tracking-[0.3em] text-cyan-300 text-sm mb-4">{results.length ? "The votes are in" : "Nobody played a card!"}</h3>
          <div className="space-y-3">
            {results.map((r, i) => (
              <motion.div key={r.id} data-testid={`tv-meme-result-${i}`} initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.3 + i * 0.25 }}
                className={`rounded-2xl px-4 py-3 flex items-center gap-4 border-2 ${r.winner ? "bg-[#FFD700]/15 border-[#FFD700] shadow-[0_0_24px_rgba(255,215,0,0.45)]" : "glass border-white/10"}`}>
                <Avatar player={{ name: r.owner_name, color: r.owner_color, avatar_url: r.owner_avatar }} size={48} />
                <div className="flex-1 min-w-0">
                  <p className="font-head font-bold text-white flex items-center gap-2">{r.owner_name}{r.winner && <Crown className="w-4 h-4 text-[#FFD700]" fill="#FFD700" />}</p>
                  <p className="text-white/70 text-sm leading-snug line-clamp-2">{r.text}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-display font-black text-2xl text-white">{r.votes}<span className="text-sm text-white/50 ml-1">vote{r.votes === 1 ? "" : "s"}</span></p>
                  {r.points > 0 && <p className="text-[#00FF66] font-bold text-sm flex items-center justify-end gap-1"><Check className="w-3.5 h-3.5" />+{r.points}</p>}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
